import type { BudgetPlanTemplate } from "@/reducers/domain";
import { BudgetPlanPolicy } from "@redbox-ruby/data-lib";
import clamp from "lodash/clamp";
import moment from "moment";
import {
    RubyBudgetPlanMethod,
    RubyBudgetPlanPolicy,
    RubyBudgetPlanStatus,
    type RubyBasicBudgetPlan,
    type RubyBudgetPlan,
    type RubyBudgetPlanDetails,
    type RubyWeekday,
    type RubyWeekdayBudgets,
} from "../services/backend/RubyData";

export function getActiveBudgetPlan(plans: RubyBasicBudgetPlan[]): RubyBasicBudgetPlan {
    if (plans.length < 1) {
        return null;
    }

    const activePlans = plans.filter((plan) => plan.status === RubyBudgetPlanStatus.ACTIVE);

    const activeByDate = activePlans.find(isActiveBudgetPlan);

    if (activeByDate) {
        return activeByDate;
    }

    const sortedPlans = activePlans.filter((plan) => plan.start < Date.now()).sort((a, b) => a.start - b.start);

    if (sortedPlans.length < 1) {
        return null;
    }

    const lastActive = sortedPlans[sortedPlans.length - 1];

    if ([BudgetPlanPolicy.ROLLOVER_AT_END, BudgetPlanPolicy.REPEATING].includes(lastActive.policy)) {
        return lastActive;
    }

    return null;
}

/**
 * This is only checking if a single plan is in date, not if it has rolled over, as that requires the full list of plans
 */
export function isActiveBudgetPlan(plan: RubyBasicBudgetPlan): boolean {
    return moment.utc().isBetween(moment.utc(plan.start).startOf("day"), moment.utc(plan.end).endOf("day"));
}

export function extendBudgetPlans(plans: RubyBasicBudgetPlan[]): RubyBudgetPlan[] {
    const activePlan = getActiveBudgetPlan(plans);
    return plans.map((plan) => {
        return extendBudgetPlan(plan, activePlan?.id);
    });
}

export function extendBudgetPlan(plan: RubyBasicBudgetPlan, activePlanId: RubyBudgetPlan["id"]): RubyBudgetPlan {
    return {
        ...plan,
        active: plan.id === activePlanId,
        lengthInDays: moment.utc(plan.end).diff(plan.start, "days") + 1,
        daysRemaining: moment.utc(plan.end).diff(moment.utc().startOf("day"), "days"),
        start: moment.utc(plan.start).startOf("day").valueOf(),
        end: moment.utc(plan.end).endOf("day").valueOf(),
    };
}

export function getAllocatedBudget(plan: RubyBudgetPlan): number {
    return (
        plan?.allocations?.reduce((sum, al) => {
            return (
                sum +
                al.details.regions.reduce((sum, region) => {
                    return sum + region.amount;
                }, 0)
            );
        }, 0) ?? null
    );
}

export function getTodaysAllocation(plan: RubyBudgetPlan) {
    const today = moment.utc();
    const todaysAllocation = plan?.allocations?.reduce((latestAllocation, allocation) => {
        if (allocation.createdAt > (latestAllocation?.createdAt ?? 0) && today.isSame(allocation.createdAt, "day")) {
            return allocation;
        }
        return latestAllocation;
    }, null);
    return todaysAllocation;
}

export const DEFAULT_WEEKLY_OVERSPEND = {
    monday: 100,
    tuesday: 100,
    wednesday: 100,
    thursday: 100,
    friday: 100,
    saturday: 100,
    sunday: 100,
};

/**
 * Ordered so the index matches `moment().day()`, letting a date map straight to
 * a weekday key.
 */
const WEEKDAY_NAMES: RubyWeekday[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
];

export const DEFAULT_WEEKLY_BUDGETS: RubyWeekdayBudgets = {
    monday: null,
    tuesday: null,
    wednesday: null,
    thursday: null,
    friday: null,
    saturday: null,
    sunday: null,
};

/**
 * A budget plan can vary its spend by day of the week in one of two ways. These
 * are alternatives rather than complementary, so a plan uses one or the other.
 */
export enum WeekdayBudgetMode {
    // Spend a multiple of the plan's calculated daily budget on a given day.
    MULTIPLIER = "multiplier",
    // Spend an explicit amount on a given day.
    AMOUNT = "amount",
}

/**
 * @param details - The details of a saved plan
 * @returns The mode the plan is using, or null when it doesn't vary by day
 */
export function getWeekdayBudgetMode(details: RubyBudgetPlanDetails): WeekdayBudgetMode | null {
    if (hasScheduledWeekdayBudgets(details?.weekdayBudgets)) {
        return WeekdayBudgetMode.AMOUNT;
    }
    if (hasWeekdayOverspend(details?.weekdayOverspend)) {
        return WeekdayBudgetMode.MULTIPLIER;
    }
    return null;
}

/**
 * The amount a plan is scheduled to spend on a given day, which replaces the
 * plan's own daily budget when it is set.
 *
 * Read in UTC to match the day the budget allocation runs against.
 *
 * @param details - The details of a saved plan
 * @param date - The day to read, today if none is given
 * @returns The scheduled amount, or null when that day isn't scheduled
 */
export function getScheduledBudgetForDate(details: RubyBudgetPlanDetails, date = moment.utc()): number | null {
    const amount = details?.weekdayBudgets?.[WEEKDAY_NAMES[moment.utc(date).day()]];
    return amount > 0 ? amount : null;
}

/**
 * Drops the days with no scheduled amount, leaving only the days a plan
 * actually overrides.
 *
 * @param weekdayBudgets - The scheduled budgets held on a plan, if any
 * @returns The scheduled days and their amounts
 */
export function getScheduledWeekdayBudgets(
    weekdayBudgets: RubyWeekdayBudgets | null | undefined
): Record<string, number> {
    return Object.entries(weekdayBudgets ?? {}).reduce<Record<string, number>>((set, [day, amount]) => {
        if (amount > 0) {
            set[day] = amount;
        }
        return set;
    }, {});
}

function hasScheduledWeekdayBudgets(weekdayBudgets: RubyWeekdayBudgets | null | undefined): boolean {
    return Object.values(weekdayBudgets ?? {}).some((amount) => amount > 0);
}

function hasWeekdayOverspend(weekdayOverspend: RubyBudgetPlanDetails["weekdayOverspend"] | null): boolean {
    return Object.values(weekdayOverspend ?? {}).some((percentage) => percentage > MIN_OVERSPEND_PERCENTAGE);
}

/**
 * Builds the details to send to the API. Only the days of the mode in use are
 * sent, so switching mode clears whatever the other mode had configured.
 *
 * @param template - The plan being saved
 * @returns The plan details in the shape the API expects
 */
export function convertTemplateToPlanDetails(template: BudgetPlanTemplate): RubyBudgetPlanDetails {
    return {
        weekdayOverspend: {
            ...DEFAULT_WEEKLY_OVERSPEND,
            ...(template?.weeklyOverspend ?? undefined),
        },
        weekdayBudgets: template?.weeklyBudgets ?? null,
    };
}

// Overspend values are %s starting at 100%. The idea being that the default
// is to spend 100% of their allocation, but some days you can allow overspend

/**
 * The API models weekday overspend as a percentage in excess of 100% (100 = no
 * overspend, 200 = spend double) and rejects anything outside this range. The
 * form works in multipliers, so every conversion has to stay inside these.
 */
export const MIN_OVERSPEND_MULTIPLIER = 1;
export const MAX_OVERSPEND_MULTIPLIER = 10;
const MIN_OVERSPEND_PERCENTAGE = MIN_OVERSPEND_MULTIPLIER * 100;
const MAX_OVERSPEND_PERCENTAGE = MAX_OVERSPEND_MULTIPLIER * 100;

/**
 * The number input has to step in hundredths or multipliers like 1.15 can't be
 * entered, and the browser reports them as invalid.
 */
export const OVERSPEND_MULTIPLIER_STEP = 0.01;

function multiplierToOverspendPercentage(multiplier: number): number | null {
    const value = Number(multiplier);
    if (!Number.isFinite(value) || value <= MIN_OVERSPEND_MULTIPLIER) {
        return null;
    }
    return clamp(Math.round(value * 100), MIN_OVERSPEND_PERCENTAGE, MAX_OVERSPEND_PERCENTAGE);
}

function overspendPercentageToMultiplier(percentage: number): number {
    const value = Number(percentage);
    if (!Number.isFinite(value) || value <= MIN_OVERSPEND_PERCENTAGE) {
        return MIN_OVERSPEND_MULTIPLIER;
    }
    return clamp(Math.round(value), MIN_OVERSPEND_PERCENTAGE, MAX_OVERSPEND_PERCENTAGE) / 100;
}

export const budgetPlanInitialValues = {
    timeRange: [moment.utc().startOf("day").toDate(), moment.utc().add(30, "days").endOf("day").toDate()] as [
        Date,
        Date
    ],
    totalAmount: null as number,
    weeklyOverspend: {
        monday: 1,
        tuesday: 1,
        wednesday: 1,
        thursday: 1,
        friday: 1,
        saturday: 1,
        sunday: 1,
    },
    weeklyBudgets: { ...DEFAULT_WEEKLY_BUDGETS },
    policy: RubyBudgetPlanPolicy.STOP_AT_END,
    method: RubyBudgetPlanMethod.ADJUST_DAILY,
    setOverspend: false,
    weekdayBudgetMode: WeekdayBudgetMode.MULTIPLIER,
};

export function convertBudgetInputsToPlan(
    values: typeof budgetPlanInitialValues = budgetPlanInitialValues
): BudgetPlanTemplate {
    const usesMultipliers = values.setOverspend && values.weekdayBudgetMode === WeekdayBudgetMode.MULTIPLIER;
    const usesAmounts = values.setOverspend && values.weekdayBudgetMode === WeekdayBudgetMode.AMOUNT;
    return {
        policy: values.policy,
        method: values.method,
        timeRange: [
            moment.utc(values.timeRange[0]).startOf("day").valueOf(),
            moment.utc(values.timeRange[1]).endOf("day").valueOf(),
        ],
        totalAmount: values.totalAmount,
        weeklyOverspend: usesMultipliers
            ? (Object.fromEntries(
                  Object.entries(values.weeklyOverspend)
                      .map(([day, value]) => {
                          const percentage = multiplierToOverspendPercentage(value);
                          if (percentage === null) {
                              return null;
                          }
                          return [day, percentage];
                      })
                      .filter((d) => !!d)
              ) as BudgetPlanTemplate["weeklyOverspend"])
            : null,
        weeklyBudgets: usesAmounts
            ? (Object.fromEntries(
                  Object.keys(DEFAULT_WEEKLY_BUDGETS).map((day) => [
                      day,
                      toScheduledBudget(values.weeklyBudgets?.[day]),
                  ])
              ) as RubyWeekdayBudgets)
            : null,
    };
}

/**
 * Days left blank are sent as null so they keep following the plan's daily
 * budget, rather than being scheduled to spend nothing.
 */
function toScheduledBudget(amount: number | null | undefined): number | null {
    const value = Number(amount);
    if (amount == null || !Number.isFinite(value) || value <= 0) {
        return null;
    }
    return value;
}

export function convertBudgetTemplateToValues(plan: BudgetPlanTemplate): typeof budgetPlanInitialValues {
    if (!plan) {
        return budgetPlanInitialValues;
    }
    const overspends = Object.entries(plan.weeklyOverspend ?? {});
    const hasOverspend = overspends.some(([_day, value]) => value > MIN_OVERSPEND_PERCENTAGE);
    const hasBudgets = hasScheduledWeekdayBudgets(plan.weeklyBudgets);
    return {
        policy: plan.policy,
        method: plan.method,
        timeRange: [new Date(plan.timeRange[0]), new Date(plan.timeRange[1])],
        setOverspend: hasOverspend || hasBudgets,
        weekdayBudgetMode: hasBudgets ? WeekdayBudgetMode.AMOUNT : WeekdayBudgetMode.MULTIPLIER,
        totalAmount: plan.totalAmount,
        weeklyOverspend: {
            ...budgetPlanInitialValues.weeklyOverspend,
            ...(Object.fromEntries(
                overspends.map(([day, value]) => [day, overspendPercentageToMultiplier(value)])
            ) as BudgetPlanTemplate["weeklyOverspend"]),
        },
        weeklyBudgets: {
            ...DEFAULT_WEEKLY_BUDGETS,
            ...(Object.fromEntries(
                Object.entries(plan.weeklyBudgets ?? {}).map(([day, amount]) => [day, toScheduledBudget(amount)])
            ) as RubyWeekdayBudgets),
        },
    };
}

export function convertBudgetPlanToTemplate(plan: RubyBudgetPlan): BudgetPlanTemplate {
    if (!plan) {
        return convertBudgetInputsToPlan();
    }
    return {
        policy: plan.policy,
        method: plan.method,
        timeRange: [plan.start, plan.end],
        totalAmount: plan.totalBudget,
        weeklyOverspend: {
            ...DEFAULT_WEEKLY_OVERSPEND,
            ...(plan.details?.weekdayOverspend ?? {}),
        },
        weeklyBudgets: {
            ...DEFAULT_WEEKLY_BUDGETS,
            ...(plan.details?.weekdayBudgets ?? {}),
        },
    };
}

export function convertBudgetPlanToValues(plan: RubyBudgetPlan): typeof budgetPlanInitialValues {
    const template = convertBudgetPlanToTemplate(plan);
    return convertBudgetTemplateToValues(template);
}
